import { routes } from '@/config/routes'
import { useMixpanel } from '@/context/mixpanel'
import type { PositionedVault, PositionedVaultsListElement } from '@/hooks/useVaultsWithPositions'
import type { VaultApyRange } from '@/types/apy'
import { formatApyTimestamp, getApyValue, getCappedApyCopy } from '@/utils/apy'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getValueColor } from '@/utils/styles/getValueColor'
import { formatUSD } from '@vaultsfyi/common/utils'
import { Link, generatePath } from 'react-router'
import { ApyTooltip } from '../common/apyTooltip'
import { Button } from '../common/button'
import { NetworkImage } from '../common/networkImage'
import { ProtocolImage } from '../common/protocolImage'
import { TokenImage } from '../common/tokenImage'
import { PositionBadge } from './positionBadge'

interface Props {
  vault: PositionedVault | PositionedVaultsListElement
  apyRange: VaultApyRange
  includeRewards: boolean
  className?: string
}

export const VaultsListCard = ({ vault, apyRange, includeRewards, className }: Props) => {
  const {
    name,
    protocol,
    asset,
    network,
    tvl,
    address,
    balanceUsd,
    apy,
    rewards,
    additionalIncentives,
    apyComposite,
    lastUpdateTimestamp,
  } = vault
  const { trackEvent } = useMixpanel()

  return (
    <article
      className={cn(
        'group relative flex w-full overflow-hidden rounded-2xl border bg-surface px-4 py-3',
        'ring-inset has-[:focus-visible]:ring',
        className
      )}
    >
      <div className="flex w-full items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <ProtocolImage protocol={protocol} className="size-7 shrink-0 p-0.5 group-even:bg-page" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <Button
                variant="default"
                size="default"
                className={cn(
                  '!ring-0 min-w-0 text-heading-foreground',
                  'before:absolute before:inset-0 before:h-full before:w-full'
                )}
                asChild
              >
                <Link
                  to={generatePath(routes.opportunity, { vaultId: vault.vaultId, network: network.name })}
                  prefetch="viewport"
                  onClick={() => trackEvent('Vault card clicked', { Vault: name, Network: network, Address: address })}
                >
                  <h3 title={name} className="overflow-hidden text-ellipsis text-nowrap font-semibold text-sm">
                    <span className="sr-only">Go to</span> {name} <span className="sr-only">vault page</span>
                  </h3>
                </Link>
              </Button>
              <PositionBadge balanceUsd={balanceUsd} className="shrink-0" />
            </div>
            <p className="flex flex-wrap items-center gap-1 text-xs">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span>Supply {asset.symbol}</span>
                <TokenImage symbol={asset.symbol} className="size-4" />
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span>
                  on <span className="capitalize">{getNetworkName(network.name)}</span>
                </span>
                <NetworkImage network={network.name} className="size-4" />
              </span>
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end">
          <div className="flex items-center gap-1">
            <ApyTooltip
              tabIndex={-1}
              asset={asset}
              additionalIncentives={additionalIncentives}
              apy={apy}
              apyRange={apyRange}
              rewards={rewards}
              apyComposite={apyComposite}
              includeRewards={includeRewards}
              className="relative z-[1]"
            />
            <p
              className={cn(
                'font-semibold text-base',
                getValueColor(getApyValue({ apy, apyRange, includeRewards, apyComposite }))
              )}
            >
              {getCappedApyCopy({ apy, apyRange, includeRewards, apyComposite })}
              <span className="hidden">(Yield last updated: {formatApyTimestamp(lastUpdateTimestamp)})</span>
            </p>
          </div>
          <p className="text-xs">{formatUSD(tvl.usd)}</p>
        </div>
      </div>
    </article>
  )
}
